import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { LeadSection } from "@/components/LeadSection";
import { SiteFooter } from "@/components/SiteFooter";
import { RoiCalculator } from "@/components/RoiCalculator";

const TITLE = "Shopify Conversion-Rechner: Umsatzpotenzial";
const DESCRIPTION =
  "Kostenloser Vorher-Nachher-Rechner: Traffic, Conversion-Rate und Warenkorb eintragen und im Diagramm sehen, wie viel Mehrumsatz ein Prozentpunkt bringt.";

const faqs = [
  {
    q: "Wie berechnet der Rechner die Umsatzdifferenz?",
    a: "Umsatz = Sessions × Conversion-Rate × durchschnittlicher Warenkorb. Der Rechner stellt diese Formel zweimal auf, einmal mit Ihrer heutigen Conversion-Rate und einmal mit einem von Ihnen angenommenen Wert, und zeigt die Differenz pro Monat und über zwölf Monate. Umsatz ist dabei nicht Gewinn.",
  },
  {
    q: "Welche Conversion-Rate ist für einen Shopify-Shop normal?",
    a: "Das schwankt stark nach Branche, Sortiment, Preisniveau und Traffic-Quelle. Aussagekräftig ist nicht ein allgemeiner Vergleichswert, sondern die Entwicklung Ihrer eigenen Zahlen über die Zeit.",
  },
  {
    q: "Wie viel Conversion-Steigerung ist realistisch?",
    a: "Das lässt sich vorab nicht seriös beziffern. Es hängt von Ausgangslage, Sortiment, Traffic und Warenkorb ab. Der Rechner zeigt deshalb ein Szenario, das Sie selbst wählen, keine Prognose.",
  },
  {
    q: "Warum ist Conversion-Optimierung günstiger als mehr Werbebudget?",
    a: "Mehr Traffic kostet jeden Monat neu Geld. Eine höhere Conversion-Rate wirkt auf jeden zukünftigen Besucher, auch auf den organischen, den Sie ohnehin bezahlt haben. Der Effekt ist kumulativ statt mietbar.",
  },
  {
    q: "Rechnet der Rechner mit Netto- oder Bruttowerten?",
    a: "Tragen Sie Nettowerte ein, dann ist auch das Ergebnis netto. Für eine Deckungsbeitragsrechnung multiplizieren Sie den Mehrumsatz zusätzlich mit Ihrer Marge.",
  },
];

export const Route = createFileRoute("/roi-rechner")({
  component: RoiPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://shopify-freelancer.net/roi-rechner" },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://shopify-freelancer.net/roi-rechner" },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/roi-rechner" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/roi-calculator" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/roi-rechner" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function RoiPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Conversion-Rechner
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-tight md:text-5xl">
              Wie viel Umsatz liegt gerade{" "}
              <span className="text-signal">ungenutzt</span> in Ihrem Store?
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground">
              Conversion-Optimierung ist keine Geschmacksfrage, sondern eine
              Rechenaufgabe. Tragen Sie Ihre echten Zahlen ein und sehen Sie sofort, was ein
              Prozentpunkt mehr Kaufabschlussrate für Ihren Monats- und Jahresumsatz
              bedeutet.
            </p>

            <div className="mt-10">
              <RoiCalculator />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              Warum diese Rechnung wichtiger ist als jedes Redesign
            </h2>
            <div className="mt-6 space-y-4 leading-relaxed text-muted-foreground">
              <p>
                Die meisten Shop-Projekte starten mit einer Geschmacksdiskussion: neue
                Farben, neues Theme, neue Bilder. Die entscheidende Frage bleibt dabei
                unbeantwortet, welchen wirtschaftlichen Unterschied macht das
                Ergebnis? Wer den Wert eines Projekts nicht beziffern kann, verhandelt
                am Ende nur noch über den Preis.
              </p>
              <p>
                Deshalb steht am Anfang jeder Zusammenarbeit diese Tabelle: Traffic,
                Conversion-Rate, Warenkorb, Wiederkaufrate. Aus vier Zahlen ergibt sich
                der Zielwert eines Projekts. Und aus dem Zielwert ergibt sich, ob 3.990 €
                Honorar eine Investition oder eine Ausgabe sind.
              </p>
              <p>
                Faustregel: Wenn der erwartete Mehrumsatz im ersten Jahr nicht mindestens
                dem Zehnfachen des Honorars entspricht, sage ich Ihnen das offen, und wir
                lassen es. Ein Projekt, das sich nicht rechnet, ist für uns beide
                schlecht.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Fragen zum Rechner</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-3xl px-5 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">
              Lassen Sie uns Ihre echten Zahlen anschauen
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-muted-foreground">
              Schicken Sie mir Shop-URL und die vier Kennzahlen. Sie bekommen innerhalb von 48
              Stunden eine ehrliche Einschätzung, welcher Hebel bei Ihnen am größten ist.
            </p>
            <Link
              to="/kontakt"
              className="shadow-signal mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Kostenlose Einschätzung erhalten
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
        <LeadSection source="roi-rechner" />
      </main>
      <SiteFooter />
    </div>
  );
}
